export type UserAttributes = Record<string, unknown>;

export const USER_AVAILABILITY_STATUS_VALUES = ['available', 'busy', 'away', 'appear_offline'] as const;

export type UserAvailabilityStatus = typeof USER_AVAILABILITY_STATUS_VALUES[number];
export type UserPresenceDisplayStatus = UserAvailabilityStatus | 'offline';

export type UserAvailabilityOption = {
  value: UserAvailabilityStatus;
  label: string;
  icon: string;
};

export const USER_AVAILABILITY_OPTIONS: readonly UserAvailabilityOption[] = [
  { value: 'available', label: 'Available', icon: 'check_circle' },
  { value: 'busy', label: 'Busy', icon: 'do_not_disturb_on' },
  { value: 'away', label: 'Away', icon: 'schedule' },
  { value: 'appear_offline', label: 'Offline', icon: 'radio_button_unchecked' },
] as const;

export interface User {
  id: string;
  organization_id: string;
  name: string;
  phone: string;
  email: string;
  role?: string;
  unique_reference_id: string;
  device_token: string;
  is_active: boolean;
  is_verified: boolean;
  attributes: UserAttributes | Array<{ Key?: string; Value?: unknown }> | null;
  organization_name: string | null;
  timezone: string;
}

export interface CreateOrganizationUserPayload {
  name: string;
  email: string;
  phone: string;
}

export interface UpdateOrganizationUserPayload {
  name: string;
  email: string;
  phone: string;
}

export function normalizeUserAttributes(attributes: User['attributes']): UserAttributes {
  if (!attributes) {
    return {};
  }

  if (Array.isArray(attributes)) {
    return attributes.reduce<UserAttributes>((accumulator, entry) => {
      const key = typeof entry?.Key === 'string' ? entry.Key : '';
      if (!key) {
        return accumulator;
      }

      accumulator[key] = entry.Value;
      return accumulator;
    }, {});
  }

  return attributes;
}

export function normalizeUserAvailabilityStatus(attributes: User['attributes']): UserAvailabilityStatus {
  const normalizedAttributes = normalizeUserAttributes(attributes);
  const rawStatus = normalizedAttributes['availability_status'];

  if (typeof rawStatus !== 'string') {
    return 'available';
  }

  const normalizedStatus = rawStatus.trim().toLowerCase();
  return USER_AVAILABILITY_STATUS_VALUES.includes(normalizedStatus as UserAvailabilityStatus)
    ? (normalizedStatus as UserAvailabilityStatus)
    : 'available';
}

export function toUserPresenceDisplayStatus(
  status: UserAvailabilityStatus,
  isOnline: boolean
): UserPresenceDisplayStatus {
  if (status === 'appear_offline') {
    return 'appear_offline';
  }

  if (!isOnline && status === 'available') {
    return 'offline';
  }

  return status;
}

export function getUserPresenceLabel(status: UserPresenceDisplayStatus): string {
  switch (status) {
    case 'busy':
      return 'Busy';
    case 'away':
      return 'Away';
    case 'appear_offline':
      return 'Offline';
    case 'offline':
      return 'Offline';
    default:
      return 'Available';
  }
}

export interface UserState {
  data: User | null;
  allData ?: User[] | null;
  error: string | null;
  message: string | null;
  success: boolean;
  validationError: any[];
}
